import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session timeout in milliseconds (60 minutes for standard, disabled for remember_me)
const SESSION_TIMEOUT = 60 * 60 * 1000;
// Warning before timeout (5 minutes)
const SESSION_WARNING = 5 * 60 * 1000;

// Helper to decode JWT and check remember_me
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => 
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [isRememberMe, setIsRememberMe] = useState(() => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      const decoded = decodeToken(savedToken);
      return decoded?.remember_me || false;
    }
    return false;
  });

  // Activity tracking
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    setSessionWarning(false);
  }, []);

  // Check session timeout (only for non-remember_me sessions)
  useEffect(() => {
    if (!token || isRememberMe) return; // Skip timeout check for remember_me sessions

    const checkSession = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivity;
      
      if (timeSinceActivity >= SESSION_TIMEOUT) {
        // Session expired
        logout();
        window.location.href = '/login?expired=true';
      } else if (timeSinceActivity >= SESSION_TIMEOUT - SESSION_WARNING) {
        // Show warning
        setSessionWarning(true);
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkSession);
  }, [token, lastActivity, isRememberMe]);

  // Track user activity
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'input', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [updateActivity]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Global axios interceptor: auto-logout on 401 Unauthorized
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          // Don't trigger on login endpoint (handled by login form)
          if (!url.includes('/auth/login') && !url.includes('/auth/register')) {
            const currentToken = localStorage.getItem('token');
            if (currentToken) {
              // Token expired or invalid → clean logout
              localStorage.removeItem('token');
              delete axios.defaults.headers.common['Authorization'];
              setToken(null);
              setUser(null);
              setMustChangePassword(false);
              setSessionWarning(false);
              setIsRememberMe(false);
              // Show a friendly message
              if (typeof window !== 'undefined' && !window.__session_expired_shown) {
                window.__session_expired_shown = true;
                setTimeout(() => { window.__session_expired_shown = false; }, 3000);
                // Use a non-blocking notification
                try {
                  const evt = new CustomEvent('session-expired');
                  window.dispatchEvent(evt);
                } catch (_e) {
                  // ignore - CustomEvent not supported
                }
              }
            }
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      setMustChangePassword(res.data.must_change_password || false);
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    const res = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe });
    localStorage.setItem('token', res.data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    setMustChangePassword(res.data.must_change_password || res.data.user.must_change_password || false);
    setLastActivity(Date.now());
    setIsRememberMe(rememberMe);
    return res.data;
  };

  const register = async (email, password, nom) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, nom });
    localStorage.setItem('token', res.data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    setMustChangePassword(false);
    setLastActivity(Date.now());
    return res.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const res = await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    });
    setMustChangePassword(false);
    // Refresh user data
    await fetchUser();
    return res.data;
  };

  const logout = () => {
    // Call logout endpoint (best effort)
    axios.post(`${API}/auth/logout`).catch(() => {});
    
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    setSessionWarning(false);
    setIsRememberMe(false);
  };

  const extendSession = () => {
    updateActivity();
    // Optionally refresh token here
  };

  // Permission check helpers
  const isAdmin = user?.role === 'admin' || user?.is_admin;
  const isComptable = user?.role === 'comptable';
  const canManageUsers = isAdmin;
  const canDeleteProjects = isAdmin;
  const canEditServerPath = isAdmin;
  
  const hasPermission = (resource, action) => {
    if (isAdmin) return true;
    
    const rolePermissions = {
      comptable: {
        projects: ['read'],
        quotes: ['read'],
        invoices: ['read', 'update'],
        export: ['financial']
      },
      employe: {
        projects: ['read', 'update'],
        quotes: ['read', 'update'],
        invoices: ['read'],
        export: ['own']
      }
    };
    
    const perms = rolePermissions[user?.role] || rolePermissions.employe;
    return perms[resource]?.includes(action) || false;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout,
      changePassword,
      mustChangePassword,
      sessionWarning,
      extendSession,
      isAdmin,
      isComptable,
      canManageUsers,
      canDeleteProjects,
      canEditServerPath,
      hasPermission
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
